export type DashboardTab = 'databases' | 'quotes' | 'outputs';

export type DashboardTabCounts = Record<DashboardTab, number>;

const DASHBOARD_TAB_ORDER: readonly DashboardTab[] = ['databases', 'quotes', 'outputs'];

function isValidQuoteSlot(slot: number): boolean {
  return Number.isSafeInteger(slot) && slot >= 1 && slot <= 10_000;
}

export function chooseInitialDashboardTab(
  preferredTab: DashboardTab,
  counts: DashboardTabCounts,
  hasManualSelection: boolean,
): DashboardTab {
  if (hasManualSelection || counts[preferredTab] > 0) {
    return preferredTab;
  }

  return DASHBOARD_TAB_ORDER.find((tab) => counts[tab] > 0) ?? preferredTab;
}

export function parseRequestedQuoteSlot(search: string): number | null {
  const params = new URLSearchParams(search);
  const rawSlot = params.get('quote') || params.get('loadQuote');

  if (!rawSlot || !/^[1-9]\d*$/.test(rawSlot)) {
    return null;
  }

  const slot = Number(rawSlot);
  return isValidQuoteSlot(slot) ? slot : null;
}

export function hasRequestedQuoteParam(search: string): boolean {
  const params = new URLSearchParams(search);
  return params.has('quote') || params.has('loadQuote');
}

export function buildQuoteEditorUrl(slot: number, search: string): string {
  if (!isValidQuoteSlot(slot)) {
    throw new RangeError('Invalid quote slot: expected an integer from 1 to 10000.');
  }

  const currentParams = new URLSearchParams(search);
  const params = new URLSearchParams({ quote: String(slot) });
  const projectId = currentParams.get('projectId');
  if (projectId !== null) {
    params.set('projectId', projectId);
  }

  return `/?${params.toString()}`;
}

export function parseRequestedSnapshotId(search: string): string | null {
  const rawId = new URLSearchParams(search).get('snapshot');
  if (rawId === null) {
    return null;
  }

  const id = rawId.trim();
  return id.length > 0 ? id : null;
}

export function buildSnapshotReviewUrl(id: string): string {
  const normalizedId = typeof id === 'string' ? id.trim() : '';
  if (normalizedId.length === 0) {
    throw new RangeError('Invalid snapshot id: expected a non-empty string.');
  }

  return `/?${new URLSearchParams({ snapshot: normalizedId }).toString()}`;
}
